import { Injectable } from "@angular/core";
import { AcceptOfferRequest, TradeType } from "@gql/offer";
import { TOKEN_BEST_ID, TRADABLES_TYPE } from "@helpers/constants";
import { round, setPrecisionDecimals, stripFunnyInput } from "@helpers/utils";
import { CryptocoinService, FiatService } from "@main-services/currency";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Conversion, Cryptocoin, Fiat, FiatWallet, Offer, Price } from "@models";
import { AcceptedOffer, AcceptedOfferDeserializer } from "@models/accepted-offer";
import { Tradable as LegacyTradeable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { SELECTED_CURRENCY, TRADE_TYPE } from "@models/trade-offer.model";
import { TradableTransaction, TransactionTag } from "@models/transaction";
import { TradablesFacade } from "@modules/+new/core/tradables/domain/+state/tradables.facade";
import { Tradable as NewTradable, Tradable } from "@modules/+new/core/tradables/domain/entities/tradable";
import { TradableInfo } from "@modules/+new/core/tradables/domain/entities/tradable-info";
import { ITBTrade } from "@modules/shared/components/instant-trade-bonus/entitites/itb-trade";
import { combineLatest, Observable, of } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";
import { CheckoutRemoteService } from "../data-access/checkout.remote";
import { IFiatWalletDataViewModel } from "../entities/checkout.fiat-selection.model";
import { iPreselectFiatValue, PriceConversions } from "../entities/checkout.model";
import { LeveragedTokensFacade } from "@modules/main/components/leveraged-tokens/state/leveraged-tokens.facade";
import { ILeverageTokenInfoSummaryViewModel } from "@modules/main/components/leveraged-tokens/leverage-token-info-summary/leverage-token-info-summary.interface";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { IFiatDepositInfoData } from "../entities/checkout.amount.model";
import { FiatDepositInfosState } from "../+state/fiat-deposit.state";
import { convertFiatToTradable, convertTradableToFiat } from "@helpers/tradable-convert";
import { SegmentService } from "@main-services/segment.service";
import { TradeOfferService } from "@modules/trade/services/trade-offer.service";
import {
	OrderTypeTrackingProperty,
	TradeTrackingName,
	TradeTypeTrackingProperty
} from "../entities/trade.rudderstack.tracking.model";
import { EstimatePricesQueryVariables } from "../../../api/graphql/offer/resources/generated/estimate.prices.service.graphql-gen";
import { RudderstackDynamicProperties, RudderstackService } from "@main-services/rudderstack.service";

@Injectable({ providedIn: "root" })
export class CheckoutDataService {
	constructor(
		private tradableFacade: TradablesFacade,
		private fiatService: FiatService,
		private apiService: CheckoutRemoteService,
		private cryptocoinService: CryptocoinService,
		private walletService: TradableWalletListService,
		private legacyTradebleService: TradableListService,
		private fiatwalletService: FiatWalletService,
		private leveragedTokenFacade: LeveragedTokensFacade,
		private cashPlusPortfolioFacade: CashplusPortfolioFacade,
		private fiatDespositInfoState: FiatDepositInfosState,
		private tradableListService: TradableListService,
		private segmentService: SegmentService,
		private rudderstackService: RudderstackService,
		private tradeOfferService: TradeOfferService
	) {}

	/**
	 * Get Fiat
	 * @param fiatId string
	 * @returns Fiat as Observable
	 */
	getFiat$(fiatId: string): Observable<Fiat | undefined> {
		return of(this.getFiatSync(fiatId));
	}

	getFiatSync(fiatId: string): Fiat | undefined {
		return this.fiatService.getItemById(fiatId);
	}

	/**
	 * get Price
	 * @param fiatWalletId string
	 * @param assetId
	 * @param fiatId
	 * @param walletId string
	 * @param tradeType
	 * @param switchDefaultToGraphql
	 * @returns Price as Observable
	 */
	getPrice$(
		fiatWalletId: string,
		assetId: string,
		fiatId: string,
		walletId: string,
		tradeType: TRADE_TYPE = TRADE_TYPE.BUY,
		switchDefaultToGraphql: boolean = false
	): Observable<Price | undefined> {
		const offer = new Offer();

		// when the page refresh and the wallet is new the value of this.walletService.getItemById(walletId) return undefined
		// therefore this check is needed
		if (!!this.walletService.getItemById(walletId) && !this.walletService.getItemById(walletId)?.isNew()) {
			offer.setWalletId(walletId);
		}
		offer.setType(tradeType);
		offer.setFiatWalletId(fiatWalletId);
		offer.setFiatId(fiatId);
		offer.setAmountDefinition(SELECTED_CURRENCY.CRYPTOCOIN);
		offer.setCurrencyId(assetId);

		const tradable = this.tradableListService.getItemById(assetId);
		const fiat = this.fiatService.getItemById(fiatId);
		const isGeneratedWallet = walletId.includes("generated");
		const estimatePricesPayload: EstimatePricesQueryVariables = {
			input: {
				assetId: tradable.getPid(),
				fiatId: fiat.getPid(),
				assetWalletId: isGeneratedWallet ? null : walletId,
				type: TradeType.Buy
			}
		};
		const isIndex = tradable.getCurrencyType() === TRADABLES_TYPE.INDEX;
		return this.apiService
			.getDefaultPriceRangeList(offer, switchDefaultToGraphql, estimatePricesPayload, isIndex)
			.pipe(map((prices) => prices[0]));
	}

	/**
	 * get the price conversion for the price range in amout selection
	 * @param fiatWalletId string
	 * @param walletId string
	 * @param assetId string
	 * @param fiatId string
	 * @param tradeType
	 * @param switchDefaultToGraphql
	 * @returns PriceConversions as Observable
	 */
	getPriceConversions$(
		fiatWalletId: string,
		assetId: string,
		fiatId: string,
		walletId: string,
		tradeType: TRADE_TYPE = TRADE_TYPE.BUY,
		switchDefaultToGraphql: boolean = false
	): Observable<PriceConversions | undefined> {
		return this.getPrice$(fiatWalletId, assetId, fiatId, walletId, tradeType, switchDefaultToGraphql).pipe(
			map((price) => {
				const amounts = price.getAmounts();

				// Notice that the min value is omitted here

				// set max
				const max: iPreselectFiatValue[] = [{ isMax: true, amount: +price.getMaxAmount().fiatAmount() }];

				// set range
				const pre: iPreselectFiatValue[] = amounts.map((c) => ({ isMax: false, amount: +c.getFiat() }));

				// conversions
				const conversions = [...pre, ...max];

				return { price, conversions };
			})
		);
	}

	/**
	 * get Tradeable
	 * @param assetId string
	 * @returns Tradable as Observable
	 */
	getTradeable$(assetId: string): Observable<Tradable | undefined> {
		// !!! IMPORTANT !!!
		// important to unsubscribe immideately when using this facade
		return this.tradableFacade.getTradableById$(assetId);
	}

	/**
	 * get legacy Tradeable the one with the legacy tradeable class
	 * @param assetId string
	 * @returns LegacyTradeable
	 */
	getLegacyTradeable(assetId: string): LegacyTradeable | undefined {
		return this.legacyTradebleService.getItemById(assetId);
	}

	/**
	 * get legacy Tradeable by pid
	 * @param assetPid string
	 * @returns LegacyTradeable
	 */
	getLegacyTradeableByPid(assetPid: string): LegacyTradeable | undefined {
		return this.legacyTradebleService.getItemByPid(assetPid);
	}

	/**
	 * get Tradeabe info only by id
	 * @param id string
	 * @returns TradableInfo as Observable
	 */
	getTradeableInfo$(id: string): Observable<TradableInfo> {
		return this.tradableFacade.getTradableInfoByTradableId$(id);
	}

	/**
	 * get list of  available Fiat Wallets
	 * @returns FiatWalletData[] as Observable
	 */
	getFiatWalletList(): Observable<IFiatWalletDataViewModel[]> {
		return combineLatest([
			this.fiatwalletService.getList(),
			this.cashPlusPortfolioFacade.getPortfolioUnfilteredState$().pipe(map((p) => p?.fiatConfigs))
		]).pipe(
			map(([list, configs]) =>
				list
					.sort((a, b) => b.getBalance() - a.getBalance())
					.map((data) => {
						const fiat = this.getFiatSync(data.getFiatId());
						let totalBalance = "";
						if (configs) {
							totalBalance = configs.find((c) => c.asset_pid === fiat?.getPid())?.asset_balance;
						}
						return { data, fiat, totalBalance };
					})
			)
		);
	}

	/**
	 * get BEST coin
	 * @returns Cryptocoin type of BEST coin
	 */
	private getBestCoin(): Cryptocoin {
		return this.cryptocoinService.getItemById(TOKEN_BEST_ID);
	}

	/**
	 * set the ITB Data format Helper
	 * @param selectedAmount
	 * @param fiat Fiat
	 * @param price Price
	 * @param tradeable tradeable
	 * @param fiatSelected
	 * @returns ITBTrade
	 */
	getItbDataHelper(
		selectedAmount: number,
		fiat: Fiat,
		price: Price,
		tradeable: Tradable,
		fiatSelected: boolean
	): ITBTrade | undefined {
		let amount = null;

		if (!fiatSelected) {
			selectedAmount = (selectedAmount * +tradeable.assetPrice.price) / fiat.toEuroRate();
		}
		const rate = fiat.toEuroRate();
		const min = round(tradeable.assetSettings.minBuyEur / rate, fiat.getPrecision());

		if (selectedAmount >= min) {
			amount = selectedAmount * rate;
		}
		if (selectedAmount > +price.getMaxAmount().fiatAmount()) {
			amount = +price.getMaxAmount().fiatAmount() * fiat.toEuroRate();
		}

		const itbRate = price.getItb();

		if (!amount || !price.getItb() || !this.getBestCoin()) {
			amount = null;
		} else {
			amount = ((+amount * +itbRate) / this.getBestCoin().getPrice()).toString();
		}

		return {
			amount,
			itbRate
		};
	}

	/**
	 * Get Offer per GraphQL API call
	 * @param amount number
	 * @param fiatId string
	 * @param assetId string
	 * @param isFiatSelected boolean
	 * @param visionBonus
	 * @returns Offer as Observable
	 */
	getOfferGraphQL$(
		amount: number,
		fiatId: string,
		assetId: string,
		isFiatSelected: boolean,
		visionBonus?: boolean
	): Observable<Offer | undefined> {
		const offer = new Offer();
		if (isFiatSelected) {
			offer.setAmountFiat(amount?.toString());
			offer.setAmountDefinition(SELECTED_CURRENCY.FIAT);
		} else {
			offer.setAmountTradable(amount?.toString());
			offer.setUserWants(SELECTED_CURRENCY.CRYPTOCOIN);
			offer.setAmountDefinition(SELECTED_CURRENCY.CRYPTOCOIN);
		}
		offer.setFiatId(fiatId);
		offer.setType(TRADE_TYPE.BUY);
		offer.setCurrencyId(assetId);
		offer.setVisionBonus(visionBonus);

		return this.apiService.createOfferGQL(offer);
	}

	/**
	 * this is a bridge to return the offer on null depending on the trade type
	 * trade type of TRADABLES_TYPE.INDEX doesn't need an offer, and therfore
	 * must return a value of null so it won't be handled
	 * @param amount number
	 * @param fiatId string
	 * @param assetId string
	 * @param isFiatSelected boolean
	 * @param visionBonus
	 * @returns Observable<Offer | null>
	 */
	getOfferBridge$(
		amount: number,
		fiatId: string,
		assetId: string,
		isFiatSelected: boolean,
		visionBonus = null
	): Observable<Offer | null> {
		return this.getTradeable$(assetId).pipe(
			take(1),
			switchMap((tradable) => {
				if (tradable.type !== TRADABLES_TYPE.INDEX) {
					return this.getOfferGraphQL$(amount, fiatId, assetId, isFiatSelected, visionBonus);
				}

				return of(null);
			})
		);
	}

	/**
	 * Send accepted offer with offer id
	 * @param assetWalletId string
	 * @param offerId string
	 * @param isDefault
	 * @returns <AcceptOfferMutation["acceptOffer"]> as Observable
	 */
	acceptOfferGraphQL$(
		offerId: string,
		assetWalletId: string,
		isDefault: boolean
	): Observable<Partial<AcceptedOffer>> {
		const input: AcceptOfferRequest = {
			assetWalletId,
			offerId
		};

		return this.apiService.acceptOfferGQL(input, isDefault);
	}

	/**
	 * Buy index asset with GraphQL (BCI)
	 * @param assetId string
	 * @param fiatId string
	 * @param fiatWalletId string
	 * @param amount string
	 * @param tradeType
	 * @param sellAll boolean
	 * @returns Observable<AcceptedOffer>
	 */
	buyIndexGraphQL$(
		assetId: string,
		fiatId: string,
		fiatWalletId: string,
		amount: number,
		tradeType = TRADE_TYPE.BUY,
		sellAll = false
	): Observable<Partial<AcceptedOffer>> {
		return this.tradeOfferService
			.tradeBci(assetId, fiatId, fiatWalletId, amount.toString(), tradeType, sellAll)
			.pipe(
				map((result) =>
					AcceptedOfferDeserializer.deserializeIndexTradeGQL(
						result,
						amount.toString(),
						tradeType,
						assetId,
						fiatId
					)
				)
			);
	}

	/**
	 * Get Tradable Wallet by Wallet Id
	 * @param walletId string
	 * @returns TradableWallet
	 */
	getTradableWalletByWalletId(walletId: string): TradableWallet | undefined {
		return this.walletService.getItemById(walletId);
	}

	/**
	 * Get Instant trade bonus for BCis
	 * This fetch doesn't require offer id
	 * @returns string as Observable
	 */
	getInstantTradeBonusFromWalletService(): Observable<string | undefined> {
		const isIndex = this.walletService.services.filter((f) => f.getCurrencyType() === "INDEX");

		if (isIndex.length) {
			return isIndex[0].getTransactionUpdateReceived().pipe(
				map((r) => {
					if (this.itbFromWalletConditionHelper(r)) {
						return r.getAmount().toString();
					}

					return undefined;
				})
			);
		}

		return of(undefined);
	}

	private itbFromWalletConditionHelper(t: TradableTransaction): boolean {
		return (
			t?.getCryptocoinId() === TOKEN_BEST_ID &&
			!!t?.getTransactionTags().find((tt: TransactionTag) => tt?.attributes.short_name === "instant_trade_bonus")
		);
	}

	/**
	 * Get default wallet with assetId
	 * @param assetId string
	 * @returns TradableWallet
	 */
	getDefaultTradableWallet(assetId: string): TradableWallet | undefined {
		const selectedWallets: TradableWallet[] = this.getTradableWallets(assetId);
		return selectedWallets.filter((w) => w.isDefault())[0];
	}

	/**
	 * Get list of tradable wallets
	 * @param assetId
	 * @returns TradableWallet[]
	 */
	getTradableWallets(assetId: string): TradableWallet[] {
		return this.walletService.getWalletsByTradableId(assetId);
	}

	/**
	 * remove fakeWallet
	 * this operation will refresh the wallet list
	 * @param wallet TradableWallet
	 */
	removeFakeWallet(wallet: TradableWallet): void {
		this.walletService.deleteItem(wallet);
	}

	/**
	 * Updates wallet and fiat wallets
	 * @param wallet TradableWallet
	 * @param fiatWallet FiatWallet
	 */
	updateBalance(wallet?: TradableWallet, fiatWallet?: FiatWallet): void {
		if (wallet) {
			this.walletService.upsert(wallet);
		}

		if (fiatWallet) {
			this.fiatwalletService.upsert(fiatWallet);
		}
	}

	public fetchLeverageInfo(
		tradableId: string,
		fiatId: string
	): Observable<ILeverageTokenInfoSummaryViewModel | undefined> {
		const legacyTradable = this.getLegacyTradeable(tradableId);

		if (legacyTradable.getCurrencyType() !== TRADABLES_TYPE.LEVERAGED_TOKEN) {
			return of(undefined);
		}

		return combineLatest([
			this.getFiat$(fiatId),
			this.leveragedTokenFacade.fetchLeverageTokenInfo$(tradableId)
		]).pipe(
			map(([fiat, info]) => {
				const attributes = info.attributes;

				const currentLeverage = attributes.effective_leverage;
				const basePrice = attributes.last_base_price;
				const overnightFee = info.attributes.overnight_fee;
				const baseSymbol = attributes.base_asset_symbol;
				const desiredLeverage = attributes.desired_leverage;
				const isLong = +desiredLeverage > 0;
				const documentUrl = attributes?.asset_documents?.[0]?.attributes.url;
				const tradable = legacyTradable;
				const releverageTime = attributes.releverage_time.date_iso8601;
				const viewModelObject = {
					currentLeverage,
					baseSymbol,
					basePrice,
					overnightFee,
					tradableId,
					fiat,
					desiredLeverage,
					isLong,
					tradable,
					documentUrl,
					releverageTime
				};

				return viewModelObject;
			})
		);
	}

	// get fiat deposit info
	public getFiatDepositInfo$(): Observable<IFiatDepositInfoData> {
		if (this.fiatDespositInfoState.isFiatDepositInfosLoaded()) {
			return this.fiatDespositInfoState.getFiatDepositInfos$();
		}

		return this.apiService
			.getDepositFiatAmounts$()
			.pipe(tap((data) => this.fiatDespositInfoState.setFiatDepositInfos(data)));
	}

	// convert tradable to fiat helper
	convertTradableToFiatAmount(amount: string | number, tradableId: string, fiat: Fiat): number {
		if (!amount) {
			return 0;
		}
		amount = stripFunnyInput(amount);
		const converted = convertTradableToFiat(this.tradableListService.getItemById(tradableId), +amount, fiat);

		// for fiat amount precision is 2
		return +setPrecisionDecimals(converted.toString(), 2);
	}

	// convert tradable to fiat amount helper
	convertTradableToFiatAmountHelper(
		amount: number,
		priceConversions: PriceConversions,
		tradable: NewTradable,
		fiat: Fiat
	): number {
		const conversion = new Conversion();
		conversion.setFiatAmount(priceConversions.price.getPrice().getFiat());
		const converted = convertTradableToFiat(conversion, amount, fiat);
		return tradable.type === TRADABLES_TYPE.INDEX ? amount : +setPrecisionDecimals(converted.toString(), 2);
	}

	// convert fiat to tradable helper
	convertFiatToTradableAmount(amount: string | number, tradableId: string, fiat: Fiat): number {
		if (!amount) {
			return 0;
		}
		return convertFiatToTradable(fiat, +amount, this.tradableListService.getItemById(tradableId));
	}

	/**
	 * Track segment event
	 * @param name event name
	 * @param properties
	 * @returns void
	 */
	trackEvent(name: TradeTrackingName, properties?: Partial<RudderstackDynamicProperties>): void {
		const staticProperties = {
			trade_type: TradeTypeTrackingProperty.buy,
			order_type: OrderTypeTrackingProperty.market_order
		};

		if (properties) {
			properties = { ...staticProperties, ...properties };
		} else {
			properties = staticProperties;
		}

		this.segmentService.track({ name, properties });
		this.rudderstackService.track({ name, properties });
	}
}
